import React, { useEffect, useState } from "react";
import { get } from "lodash";
import Row from "@amzn/meridian/row";
import Toggle from "@amzn/meridian/toggle";
import Text from "@amzn/meridian/text";

import { SpaceGrid } from "./SpaceGrid";
import {
    costFeatureTableFourMapping,
    costFeatureTableOneMapping,
    costFeatureTableThreeMapping,
    costFeatureTableTwoMapping,
    costSiteImprovementTableOneMapping,
    costSiteImprovementTableThreeMapping,
    costSiteImprovementTableTwoMapping,
    costSpaceTableOneMapping,
    costSpaceTableThreeMapping,
    costSpaceTableTwoMapping,
} from "./spaceTableDefinition";
import { formatStatus } from "../../table/utils";

interface ISpaceComponentProps {
    data: any;
    spaceType: string;
    spaceId: string;
    isEditing: boolean;
    handleInputChange: (key: string, value: any) => void;
    viewType?: string;
}

export const SpaceComponent: React.FC<ISpaceComponentProps> = ({
    data,
    spaceType,
    spaceId,
    isEditing,
    handleInputChange,
}) => {
    const [checked, setChecked] = useState(
        get(
            data,
            `appealDetails.appealApproachCalculationData.cost.costApproachData.spaceComputationData.${spaceId}.enable`
        )
    );
    const status = get(
        data,
        `appealDetails.appealApproachCalculationData.cost.costApproachData.spaceComputationData.${spaceId}.status`,
        ""
    );

    useEffect(() => {
        const isIncluded = get(
            data,
            `appealDetails.appealApproachCalculationData.cost.costApproachData.spaceComputationData.${spaceId}.enable`
        );
        setChecked(isIncluded);
    }, [spaceId, spaceType, data]);

    const handleToggleChange = (newValue: boolean) => {
        setChecked(newValue);
        handleInputChange(
            `appealDetails.appealApproachCalculationData.cost.costApproachData.spaceComputationData.${spaceId}.enable`,
            newValue
        );
    };

    const renderCostSpace = () => (
        <>
            <Row width="fill" widths={["fill", "fill"]}>
                <Row alignmentHorizontal="start">
                    <Toggle checked={checked} onChange={handleToggleChange} disabled={!isEditing}>
                        Include this space in appeals package
                    </Toggle>
                </Row>
                <Row alignmentHorizontal="end">
                    <Text type="b300">Space status: </Text> {formatStatus(status)}
                </Row>
            </Row>
            <SpaceGrid
                mapping={costSpaceTableOneMapping(data, spaceId)}
                isEditing={isEditing}
                handleInputChange={handleInputChange}
                data={data}
            />
            <SpaceGrid
                title="Height and size refinements"
                mapping={costSpaceTableTwoMapping(data, spaceId)}
                isEditing={isEditing}
                handleInputChange={handleInputChange}
                data={data}
            />
            <SpaceGrid
                mapping={costSpaceTableThreeMapping(data, spaceId)}
                isEditing={isEditing}
                handleInputChange={handleInputChange}
                data={data}
            />
        </>
    );

    const renderCostFeature = () => (
        <>
            <Text type="h200">Features</Text>
            {Object.entries(
                data.appealDetails.appealApproachCalculationData.cost.costApproachData
                    .featuresComputationData.features
            ).map(([key, feature]: [string, any]) => {
                switch (feature.featureType) {
                    case "Mezzanine":
                    case "Canopy":
                    case "Cooling HVAC":
                    case "Sprinkler cost":
                    case "Guard House":
                        return (
                            <SpaceGrid
                                key={key}
                                mapping={costFeatureTableOneMapping(data, key, feature.featureType)}
                                isEditing={isEditing}
                                handleInputChange={handleInputChange}
                                data={data}
                            />
                        );
                    case "Passenger Elevator":
                    case "Freight Elevator":
                    case "Generator - Gas":
                    case "Generator - Diesel":
                    case "Water Storage Tank":
                        return (
                            <SpaceGrid
                                key={key}
                                mapping={costFeatureTableTwoMapping(data, key, feature.featureType)}
                                isEditing={isEditing}
                                handleInputChange={handleInputChange}
                                data={data}
                            />
                        );
                    default:
                        return null;
                }
            })}
            <SpaceGrid
                mapping={costFeatureTableThreeMapping(data)}
                isEditing={isEditing}
                handleInputChange={handleInputChange}
                data={data}
            />
            <SpaceGrid
                mapping={costFeatureTableFourMapping(data)}
                isEditing={isEditing}
                handleInputChange={handleInputChange}
                data={data}
            />
        </>
    );

    const renderCostSiteImprovement = () => (
        <>
            <Text type="h200">Site Improvements</Text>
            {Object.entries(
                data.appealDetails.appealApproachCalculationData.cost.costApproachData
                    .siteImprovementComputationData.siteImprovements
            ).map(([key, feature]: [string, any]) => (
                <SpaceGrid
                    key={key}
                    mapping={costSiteImprovementTableOneMapping(data, key, feature.improvementType)}
                    isEditing={isEditing}
                    handleInputChange={handleInputChange}
                    data={data}
                />
            ))}
            <SpaceGrid
                mapping={costSiteImprovementTableTwoMapping(data)}
                isEditing={isEditing}
                handleInputChange={handleInputChange}
                data={data}
            />
            <SpaceGrid
                mapping={costSiteImprovementTableThreeMapping(data)}
                isEditing={isEditing}
                handleInputChange={handleInputChange}
                data={data}
            />
        </>
    );

    const renderArtifactTabs = () => {
        switch (spaceType) {
            case "CostSpace":
                return renderCostSpace();
            case "CostFeature":
                return renderCostFeature();
            case "CostSiteImprovement":
                return renderCostSiteImprovement();
            default:
                return null;
        }
    };

    return <>{renderArtifactTabs()}</>;
};
